import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/store/shared/supabase";
import { dispatchDataError, describeSupabaseError } from "@/store/shared/dataErrors";
import { fetchScopeIds, EMPTY_SCOPE_IDS } from "@/store/shared/scopeIds";
import { useSession } from "@/session/SessionProvider";
import { createUuid } from "@/store/shared/id";
import { documentTypeFromDb, documentTypeToDb } from "@/store/shared/documentMapping";
import {
  buildDocumentPath,
  createSignedDocumentUrl,
  dataUrlToBlob,
  removeDocumentObject,
  uploadDocumentBlob,
} from "@/store/shared/storage";
import type { DocumentInput, DocumentItem, DocumentType } from "./types";

interface DocumentsContextValue {
  documents: DocumentItem[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  addDocument: (input: DocumentInput) => DocumentItem;
  getDocument: (id: string) => DocumentItem | undefined;
  removeDocument: (id: string) => void;
  updateDocument: (id: string, input: DocumentInput) => void;
  documentsByImmeuble: (immeubleId: string) => DocumentItem[];
  documentsByBien: (bienId: string) => DocumentItem[];
  countByImmeuble: (immeubleId: string) => number;
  countByBien: (bienId: string) => number;
  countByType: (type: DocumentType) => number;
  stats: { total: number };
}

const DocumentsContext = createContext<DocumentsContextValue | null>(null);

type Row = Record<string, unknown>;
const str = (value: unknown): string => (typeof value === "string" ? value : "");
const nullable = (value: string): string | null => (value && value.trim() ? value : null);
const num = (value: unknown): number => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

function fileNameFromPath(path: string): string {
  const segment = path.split("/").pop() ?? path;
  return segment.replace(/^\d+-[a-z0-9]{6}-/, "");
}

/**
 * Documents de l'espace Propriétaire — branché sur Supabase + Storage.
 * - Métadonnées : table `documents` (Supabase = source de vérité).
 * - Fichiers : bucket privé `documents`, chemin `‹proprietaire_id›/‹fichier›`.
 * - La consultation utilise des URLs signées temporaires (bucket privé).
 * - Un upload Storage réussi mais une insertion DB échouée supprime le fichier
 *   orphelin : jamais de « faux succès ».
 */
export function DocumentsProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scopeRef = useRef(EMPTY_SCOPE_IDS);
  const pathRef = useRef<Map<string, string>>(new Map());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      scopeRef.current = await fetchScopeIds();
      const { data, error: fetchError } = await supabase
        .from("documents")
        .select("*")
        .order("created_at", { ascending: false });
      if (fetchError) throw fetchError;

      const rows = (data as Row[] | null) ?? [];
      const paths = new Map<string, string>();
      const items = await Promise.all(
        rows.map(async (row) => {
          const id = str(row.id);
          const path = str(row.url);
          paths.set(id, path);
          const signed = await createSignedDocumentUrl(path);
          return {
            id,
            nom: str(row.nom),
            type: documentTypeFromDb(str(row.type_document)) as DocumentType,
            date: str(row.date_document),
            immeubleId: str(row.immeuble_id),
            bienId: str(row.bien_id),
            contratId: str(row.contrat_id),
            locataireId: str(row.locataire_id),
            fichierUrl: signed || path,
            fichierNom: fileNameFromPath(path),
            fichierType: str(row.type_fichier),
            fichierTaille: num(row.taille),
            createdAt: str(row.created_at),
          } satisfies DocumentItem;
        })
      );
      pathRef.current = paths;
      setDocuments(items);
    } catch (caught) {
      setError(describeSupabaseError(caught));
      console.error("[Documents] chargement Supabase échoué", caught);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!session) {
      setDocuments([]);
      return;
    }
    void load();
  }, [session, load]);

  const addDocument = useCallback(
    (input: DocumentInput) => {
      const optimistic: DocumentItem = { ...input, id: createUuid(), createdAt: new Date().toISOString() };
      setDocuments((prev) => [optimistic, ...prev]);

      void (async () => {
        let uploadedPath = "";
        try {
          const scope = scopeRef.current.proprietaireId ? scopeRef.current : await fetchScopeIds();
          const scopeId = scope.proprietaireId;
          if (!scopeId) throw new Error("Périmètre propriétaire introuvable.");

          const blob = await dataUrlToBlob(input.fichierUrl);
          const path = buildDocumentPath(scopeId, input.fichierNom || input.nom);
          await uploadDocumentBlob(path, blob);
          uploadedPath = path;

          const { data, error: insertError } = await supabase
            .from("documents")
            .insert({
              nom: input.nom,
              type_document: documentTypeToDb(input.type),
              url: path,
              date_document: input.date || null,
              immeuble_id: nullable(input.immeubleId),
              bien_id: nullable(input.bienId),
              contrat_id: nullable(input.contratId),
              locataire_id: nullable(input.locataireId),
              type_fichier: nullable(input.fichierType),
              taille: Math.max(0, Number(input.fichierTaille) || 0),
            })
            .select("id")
            .maybeSingle();
          if (insertError) throw insertError;

          const realId = (data as Row | null)?.id;
          if (realId) {
            pathRef.current.set(String(realId), path);
          }
          void load();
        } catch (caught) {
          if (uploadedPath) await removeDocumentObject(uploadedPath);
          setDocuments((prev) => prev.filter((item) => item.id !== optimistic.id));
          dispatchDataError({
            scope: "Documents",
            message: "Impossible d'enregistrer le document.",
            technical: describeSupabaseError(caught),
          });
        }
      })();

      return optimistic;
    },
    [load]
  );

  const updateDocument = useCallback(
    (id: string, input: DocumentInput) => {
      const previous = documents.find((item) => item.id === id);
      setDocuments((prev) => prev.map((item) => (item.id === id ? { ...item, ...input } : item)));
      void (async () => {
        let uploadedPath = "";
        try {
          const payload: Record<string, unknown> = {
            nom: input.nom,
            type_document: documentTypeToDb(input.type),
            date_document: input.date || null,
            immeuble_id: nullable(input.immeubleId),
            bien_id: nullable(input.bienId),
            contrat_id: nullable(input.contratId),
            locataire_id: nullable(input.locataireId),
          };
          // Nouveau fichier fourni (data URL d'aperçu) → ré-upload + suppression de l'ancien.
          const isNewFile = input.fichierUrl.startsWith("data:");
          if (isNewFile) {
            const scope = scopeRef.current.proprietaireId ? scopeRef.current : await fetchScopeIds();
            const scopeId = scope.proprietaireId;
            if (!scopeId) throw new Error("Périmètre propriétaire introuvable.");
            const blob = await dataUrlToBlob(input.fichierUrl);
            const path = buildDocumentPath(scopeId, input.fichierNom || input.nom);
            await uploadDocumentBlob(path, blob);
            uploadedPath = path;
            payload.url = path;
            payload.type_fichier = nullable(input.fichierType);
            payload.taille = Math.max(0, Number(input.fichierTaille) || 0);
          }

          const { error: updateError } = await supabase.from("documents").update(payload).eq("id", id);
          if (updateError) throw updateError;

          if (isNewFile && uploadedPath) {
            const oldPath = pathRef.current.get(id);
            if (oldPath) await removeDocumentObject(oldPath);
            pathRef.current.set(id, uploadedPath);
          }
          void load();
        } catch (caught) {
          if (uploadedPath) await removeDocumentObject(uploadedPath);
          if (previous) setDocuments((prev) => prev.map((item) => (item.id === id ? previous : item)));
          dispatchDataError({
            scope: "Documents",
            message: "Impossible de modifier le document.",
            technical: describeSupabaseError(caught),
          });
        }
      })();
    },
    [documents, load]
  );

  const removeDocument = useCallback(
    (id: string) => {
      const previous = documents.find((item) => item.id === id);
      const path = pathRef.current.get(id);
      setDocuments((prev) => prev.filter((item) => item.id !== id));
      void (async () => {
        const { error: deleteError } = await supabase.from("documents").delete().eq("id", id);
        if (deleteError) {
          if (previous) setDocuments((prev) => [previous, ...prev]);
          dispatchDataError({
            scope: "Documents",
            message: "Impossible de supprimer le document.",
            technical: describeSupabaseError(deleteError),
          });
          return;
        }
        if (path) await removeDocumentObject(path);
        void load();
      })();
    },
    [documents, load]
  );

  const getDocument = useCallback((id: string) => documents.find((item) => item.id === id), [documents]);

  const documentsByImmeuble = useCallback(
    (immeubleId: string) => documents.filter((item) => item.immeubleId === immeubleId),
    [documents]
  );

  const documentsByBien = useCallback(
    (bienId: string) => documents.filter((item) => item.bienId === bienId),
    [documents]
  );

  const countByImmeuble = useCallback(
    (immeubleId: string) => documents.filter((item) => item.immeubleId === immeubleId).length,
    [documents]
  );

  const countByBien = useCallback(
    (bienId: string) => documents.filter((item) => item.bienId === bienId).length,
    [documents]
  );

  const countByType = useCallback(
    (type: DocumentType) => documents.filter((item) => item.type === type).length,
    [documents]
  );

  const stats = useMemo(() => ({ total: documents.length }), [documents]);

  const value = useMemo(
    () => ({
      documents,
      loading,
      error,
      reload: load,
      addDocument,
      getDocument,
      removeDocument,
      updateDocument,
      documentsByImmeuble,
      documentsByBien,
      countByImmeuble,
      countByBien,
      countByType,
      stats,
    }),
    [
      documents,
      loading,
      error,
      load,
      addDocument,
      getDocument,
      removeDocument,
      updateDocument,
      documentsByImmeuble,
      documentsByBien,
      countByImmeuble,
      countByBien,
      countByType,
      stats,
    ]
  );

  return <DocumentsContext.Provider value={value}>{children}</DocumentsContext.Provider>;
}

export function useDocuments(): DocumentsContextValue {
  const context = useContext(DocumentsContext);
  if (!context) {
    throw new Error("useDocuments doit être utilisé à l'intérieur de DocumentsProvider");
  }
  return context;
}